import React from 'react';
import { createSelector } from 'reselect';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import Validator from 'validator';
import find from 'lodash-es/find';
import trimStart from 'lodash-es/trimStart';


const minLength = (minLength) => value => (
    value && value.length < minLength ? <FormattedMessage id="common.form.min.length" values={{min: minLength}} /> : undefined
);

const minTrimLength = (minLength) => value => (
    value && value.trim().length < minLength ? <FormattedMessage id="common.form.trim.min.length" values={{min: minLength}} /> : undefined
);

const maxLength = (maxLength) => value => (
    value && value.length > maxLength ? <FormattedMessage id="common.form.max.length" values={{max: maxLength}} /> : undefined
);

// 하나의 이름으로 선택된 체크박스의 값이 배열로 들어갈 때의 값 처리 (관련 컴포넌트 : CustomCheckboxGroup)
export function normalizeCheckboxValues(value, previousValue) {
    if( typeof value !== 'string' ) return previousValue;
    if( !previousValue || !previousValue.length ) return [value];
    if( !find(previousValue, val => val === value) )
        return [...previousValue, value]
    return previousValue.filter(val => val !== value)
}

// Form의 초기값
export const getFormInitValues = createSelector(
    (state, defaultValues) => state.main.item,
    (state, defaultValues) => defaultValues,
    (item, defaultValues) => {
        return ({
            ...defaultValues,
            ...item
        })
    }
);

export const lower = value => value && value.toLowerCase();
export const trim = value => value && value.trim();
export const trimLeft = value => value && trimStart(value);
export const trimAndLower = value => value && value.trim().toLowerCase();
export const trimAndUpper = value => value && value.trim().toUpperCase();

export const required = value => (
    value || typeof value === 'number' ? undefined : <FormattedMessage id="common.form.required" />
);

export const number = value => (
    value && isNaN(Number(value)) ? <FormattedMessage id="common.form.number" />: undefined
);

export const integer = value => (
    value && (isNaN(Number(value)) || !Number.isInteger(Number(value))) ? <FormattedMessage id="common.form.number.integer" />: undefined
);

export const fqdn = value => (
    value && !Validator.isFQDN(value) ? <FormattedMessage id="common.wrong.fqdn" />: undefined
);

export const email = value => (
    value && !Validator.isEmail(value) ?  <FormattedMessage id="common.wrong.address" /> : undefined
);

export const requiredForArray = value => (
    value && value.length > 0 ? undefined : <FormattedMessage id="common.form.required" />
);

// names중 적어도 하나의 데이타(trim처리)는 있어야 함
export const requiredMoreThanOne = (menu, names) => (value, allValues) => (
    names.some( name => allValues[name] ) ? undefined : <FormattedMessage id={`${menu}.empty.${names[0]}`} />
);

// Internet Message Format 중 이름에 해당하는 부분
export const nameForEmail = value => {
    const namePattern = /[\\"]/;
    return namePattern.test(value) ? <FormattedMessage id="common.name.invalid.characters" /> : undefined;
};

export const password = value => {
    const specialCharacters = /[^A-Za-z\d-!@#$%^&*(_)<>;+=~?.,]/;
    if( specialCharacters.test(value) ) return <FormattedHTMLMessage id="response.error.604" />;

    const passwordPattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[-!@#$%^&*(_)<>;+=~?.,])[A-Za-z\d-!@#$%^&*(_)<>;+=~?.,]{8,16}$/;
    return passwordPattern.test(value) ? undefined : <FormattedMessage id="response.error.603" />;
};

// Form의 초기값 (Modal창 전용)
export const getModalFormInitValues = createSelector(
    (state, defaultValues) => state.popup.body && state.popup.body.item ? state.popup.body.item : null,
    (state, defaultValues) => defaultValues,
    (item, defaultValues) => (
        item ? ({ ...defaultValues, ...item }) : defaultValues
    )
);

// formValues값 정리 (현재는 사용되는 곳 없음)
export function resetFormValues( values, menuName ) {
    let result = {};
    return result;
}

export const minTrimLength3 = minTrimLength(3);
export const minLength8 = minLength(8);
export const minTrimLength2 = minTrimLength(2);
export const maxLength16 = maxLength(16);



/** Custom(spikees용) **/
export const minLength3 = minLength(3);

export const isBucketName = (bucketName) => {
    const bucketNamePattern = /^[a-z0-9]([a-z0-9\-.]{1,30})[a-z0-9]$/i;
    return !bucketNamePattern.test(bucketName) ? <FormattedMessage id="document.bucket.name.invalid" /> : undefined;
}

export function submitForm(values, dispatch, props) {
    const menuName = props.modalId || props.menu;
    console.log("submitForm - menuName:", menuName, " / values:", values);
    console.log("submitForm props:",props);

    // "handleSubmit(submitValidation)"으로는 MenuBar에서 submit validation이 불가능하므로 
    // props를 이용해서 처리
    if( props.onSubmitValidation ) props.onSubmitValidation(values, props);

    // 수정인 경우 키값 추가
    if( props.keyValue > 0 ) values[props.keyName] = props.keyValue;
    // (관리자용) 회사별 설정의 메뉴인 경우 companyId 파라미터 추가 
    //if( menuName.indexOf("company") === 0 && props.companyInfo ) values["companyId"] = props.companyInfo.id;
    const params = typeof props.onAddParams === "function" ? props.onAddParams(values, menuName) : {};

    // 별도의 API를 호출해야 하는 경우 
    if( props.onSubmitAction ) {
        props.onSubmitAction(menuName, { ...values, ...params }, props.isPopup);
    // 수정 API 호출 
    } else if( props.keyValue ) {
        props.dispatchCrudUpdate(menuName, { ...values, ...params }, props.redirectTo ? props.redirectTo : props.isPopup);
    // 추가 API 호출
    } else {
        props.dispatchCrudCreate(menuName, { ...values, ...params }, props.redirectTo ? props.redirectTo : props.isPopup);
    }
}

// 시작일/종료일 비교
const validateStartEndDate = (startDate, endDate) => (value, allValues) => (
    allValues[startDate] && allValues[endDate] && (allValues[startDate] > allValues[endDate]) ? <FormattedMessage id="common.wrong.period"/> : undefined
);

// 문서 검색 시 검색기간 validation
export const validateSearchPeriodCondition = validateStartEndDate('periodStartCond', 'periodEndCond');